import { useState } from 'react';
import { api } from '../lib/api';
import { useToast } from './Toast';

/**
 * ImpersonationBanner — the persistent "you are viewing as someone else" bar.
 *
 * Renders at the very top of the app (above every shell) while a super_admin is
 * impersonating another user via the "View as" control. Shows who you are viewing
 * as + which role, and a "Return to {actor}" button that ends impersonation
 * (POST /api/switch-user/self) and hard-reloads so the app re-renders as the
 * original super_admin.
 *
 * Amber surface (--color-status-amber) so it reads unmistakably as a temporary,
 * elevated-privilege state. Self-contained: owns its return call + loading state.
 *
 * Lane build/switch-user.
 */

export interface ImpersonationBannerProps {
  /** Display name of the user currently being viewed as. */
  viewingName: string;
  /** Role of the user currently being viewed as. */
  viewingRole: string;
  /** Display name of the original super_admin actor to return to. */
  actorName: string;
}

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  administrator: 'Office Administrator',
  agent: 'Agent',
  analyst: 'Analyst',
  consultant: 'Consultant',
  client: 'Client',
};

const roleLabel = (r: string) => ROLE_LABELS[r] ?? r;

export function ImpersonationBanner({ viewingName, viewingRole, actorName }: ImpersonationBannerProps) {
  const { toast } = useToast();
  const [returning, setReturning] = useState(false);

  async function handleReturn() {
    setReturning(true);
    try {
      await api.post('/api/switch-user/self');
      // Hard reload so the whole SPA re-renders under the restored super_admin session.
      window.location.assign('/');
    } catch {
      setReturning(false);
      toast({ variant: 'error', message: 'Could not return to your account. Try again.' });
    }
  }

  return (
    <div
      role="status"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--sp-3)',
        flexWrap: 'wrap',
        minHeight: 40,
        padding: '6px var(--sp-5)',
        background: 'var(--color-status-amber)',
        color: '#fff',
        fontSize: 'var(--fs-sm)',
        fontWeight: 'var(--fw-medium)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
      }}
    >
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
      <span>
        Viewing as <strong>{viewingName}</strong>
        {' · '}
        <span style={{ opacity: 0.95 }}>{roleLabel(viewingRole)}</span>
      </span>
      <button
        type="button"
        onClick={handleReturn}
        disabled={returning}
        style={{
          fontFamily: 'inherit',
          fontSize: 'var(--fs-sm)',
          fontWeight: 'var(--fw-semibold)',
          padding: '4px 12px',
          borderRadius: 'var(--r-md)',
          border: '1px solid rgba(255,255,255,0.7)',
          background: 'rgba(255,255,255,0.15)',
          color: '#fff',
          cursor: returning ? 'not-allowed' : 'pointer',
          opacity: returning ? 0.7 : 1,
        }}
      >
        {returning ? 'Returning…' : `Return to ${actorName}`}
      </button>
    </div>
  );
}
